import { AfterViewInit, ChangeDetectionStrategy, Component, TemplateRef, ViewChild } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { companyCommand } from '../../command/company.command';
import { CompanyCommandHandlerResolver } from '../../service/command/company-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { Company } from '../../model/company';
import { CompanyService } from './../../service/data/company.service';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { LocalDate } from '../../../core/model/local-date';
import { forkJoin } from 'rxjs';
import { CustomProperty } from '../../model/custom-property';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { CompanyCloseAccountingPeriodCommandHandler } from '../../service/command/company-close-accounting-period-command-handler.service';
import { FilteredCommand, MultiselectCommand } from '../../../core/model/command';
import { SidebarService } from '../../../core/service/sidebar.service';
import ListViewColumn from '../../../core/model/list-view-column';
import { ColumnDefinition } from '../../../core/model/column-definition';

@Component({
    selector: 'app-company-manager',
    templateUrl: 'company-manager.component.html',
    styleUrls: ['./../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss'],
    providers: [CompanyCommandHandlerResolver, CompanyCloseAccountingPeriodCommandHandler],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyManagerComponent extends MultiselectObjectManagerComponentBase<Company> implements AfterViewInit {
  @ViewChild('bulkSystemSettingsSidebar', { static: false }) bulkSystemSettingsSidebar: TemplateRef<any>;

  menuDefinition: MenuItemDefinition[];
  excelColumnDefinition: ColumnDefinition<Company>[] = [];
  excelContext = 'Company';
  excelFileName = 'Foretag';
  selectedColumns: ListViewColumn<Company>[] = [];
  selectedCompanyIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<Company>[] = [];
  private columnUpdateScheduled = false;
  constructor(
    columnDefinitionService: ColumnDefinitionService,
    dataService: CompanyService,
    dispatcher: CommandDispatcher,
    messageService: MessageService,
    navigationService: NavigationService,
    handlerResolver: CompanyCommandHandlerResolver,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _userAccountService: UserAccountService,
    sidebarService: SidebarService
  ) {
    super(dataService, dispatcher, handlerResolver, messageService, navigationService, sidebarService);
    this.columnDefinition = columnDefinitionService.create('Company');
    this.excelColumnDefinition = [...this.columnDefinition];
    this.title = 'COMPANY.LIST_HEADER';
    this.sidebarMenuActionCommandIds = [companyCommand.bulkSystemSettings];

    this.menuDefinition = [
      new MenuItemDefinition('COMPANY.COMMAND.BULK_EDIT', companyCommand.bulkSystemSettings),
    ];

    this.preferencesKey = 'rent.company-manager.list';
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.registerSidebarMenuAction({
      commandId: companyCommand.bulkSystemSettings,
      template: this.bulkSystemSettingsSidebar,
      createDataContext: command => ({
        companies: command instanceof MultiselectCommand ? command.items : this.itemsSource ?? [],
        companyIds:
          command instanceof MultiselectCommand
            ? command.items.map(company => company.id)
            : command instanceof FilteredCommand
              ? command.includedIds
              : [],
        excludedCompanyIds: command instanceof FilteredCommand ? command.excludedIds : [],
        filter: command instanceof FilteredCommand ? command.filterProvider?.filterState ?? this.filterState ?? {} : null,
        totalAffectedItems: command instanceof FilteredCommand ? command.totalAffectedItemsControlValue : null,
      }),
    });
  }

  onBulkSystemSettingsCompleted(): void {
    this.closeSidebarMenuAction();
    this.reloadCurrentList();
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  get excelReady(): boolean {
    return this.selectedColumns.length > 0;
  }

  get selectedCompanyCount(): number {
    return this.selectedCompanyIds.length;
  }

  get allCompaniesSelected(): boolean {
    return this.selectedCompanyCount > 0 && this.selectedCompanyCount === this.itemsSource?.length;
  }

  get totalCompanies(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<Company>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedCompaniesChange(companies: Company[]): void {
    this.selectedCompanyIds = companies.map(company => company.id);
  }

  override onFilterChange(filterState: any): void {
    this.selectedCompanyIds = [];
    super.onFilterChange(filterState);
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin([(<CompanyService>this.dataService).getFilterDefinition(), this._customPropertyService.getList()]).subscribe(
        ([definition, customProperties]) => {
          this.createFilterDefinitionItems(definition, customProperties);
          this._ch.createCustomPropertyColumnDefinition(customProperties, this.columnDefinition, CustomPropertyVisibilityType.Company);
          this.excelColumnDefinition = [...this.columnDefinition];
        }
      )
    );

    this.addEditDeleteCommands();

    this.AddSubscription(
      this._userAccountService.current.subscribe(account => {
        if (account.hasAccess(ModuleNames.Accounting, Features.CloseAccountingPeriod)) {
          this.addMultiselectMenuItem('COMPANY.COMMAND.LOCK_ACCOUNTING_PERIOD', companyCommand.closeAccountingPeriod);
        }
      })
    );
  }

  private createFilterDefinitionItems(
    definition: {
      name: string;
      number: string;
      organizationNo: string;
      selectedFields: string[];
      defaultFields: string[];
      companyCustomPropertiesTextValues: { key: string; value: string }[];
      companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
      companyCustomPropertiesNumericValues: { key: string; value: string }[];
      companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    },
    customProperties: CustomProperty[]
  ) {
    this.defaultFilters = definition.defaultFields;

    // Standardfilters
    const baseFilters: FilterDefinitionItem[] = [
      new FilterDefinitionItem('COMPANY.NAME', 'name', definition.name, null, definition.selectedFields.includes('name')),
      new FilterDefinitionItem('COMMON.NUMBER', 'number', definition.number, null, definition.selectedFields.includes('number')),
      new FilterDefinitionItem(
        'COMPANY.ORGANIZATION_NUMBER',
        'organizationNo',
        definition.organizationNo,
        null,
        definition.selectedFields.includes('organizationNo')
      ),
    ];

    const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
      customProperties,
      definition,
      CustomPropertyVisibilityType.Company
    );
    this.filterDefinition = [...baseFilters, ...customPropertiesFilters];
  }
}
